import React from 'react';
import Grid from '@mui/material/Grid';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import Loading from 'components/loading';
import Fade from 'components/fade';
import Title from 'components/title';
import Form, { InputText, InputSelect, InputAutocomplete, InputTextMasked, Button, ButtonUpload } from 'components/form';
import Confirm from 'components/confirm';

import useMakeComplaint from './useMakeComplaint';

import logo from 'assets/images/logo-grupo-altum.png';
import alarm from 'assets/images/alarm.svg';

import { 
  Header, 
  Goback, 
  IconGoback, 
  Subheader, 
  Container, 
  Logo, 
  IconComplaint, 
  IconAdd, 
  WrapReports, 
  Reports, 
  WrapReportTitle, 
  TitleReport, 
  ButtonRemove, 
  IconRemove } from './styled';

const MakeComplaint: React.FC = () => {
  const {
    loading,
    fields,
    error,
    confirm,
    translate,
    goBack,
    handleChange,
    handleChangeAutocomplet,
    handleAddReport,
    handleRemoveReport,
    handleChangeReport,
    handleChangeFile,
    handleRemoveFile,
    handleCloseConfirm,
    handleSubmit,
    handleConfirm,
  } = useMakeComplaint();

  if (loading) {
    return <Loading inner />;
  }

  return (
    <>
      <Fade show={true}>
        <Header>
          <Goback color="secondary" onClick={goBack}>
            <IconGoback />
            {translate('makeComplaint.goback')}
          </Goback>
          <Logo src={logo} />
        </Header>
        <Subheader>
          <IconComplaint src={alarm} />
          <Title variant="h3" component="h1" text={translate('makeComplaint.title')} />
        </Subheader>
        <Container maxWidth="lg">
          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <InputText
                  name="name"
                  label={translate('makeComplaint.form.name')}
                  value={fields.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={4} xl={3}>
                <InputAutocomplete
                  name="state"
                  label={translate('makeComplaint.form.state')}
                  options={fields.optionsStates}
                  value={fields?.optionsStates.find((item) => item.value === fields.state)}
                  onChange={handleChangeAutocomplet}
                  error={Boolean(error.state)}
                  helperText={error.state}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={4} xl={5}>
                <InputAutocomplete
                  name="city"
                  disabled={fields.optionsCities.length === 0}
                  label={translate('makeComplaint.form.city')}
                  options={fields.optionsCities}
                  value={fields?.optionsCities?.find((item) => item.value === fields.city) ?? null}
                  onChange={handleChangeAutocomplet}
                  error={Boolean(error.city)}
                  noOptionsText={translate('makeComplaint.form.state')}
                  helperText={error.city}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={4} xl={4}>
                <InputText
                  name="location"
                  label={translate('makeComplaint.form.location')}
                  value={fields.location}
                  onChange={handleChange}
                  error={Boolean(error.location)}
                  helperText={error.location}
                />
              </Grid>
              {fields.optionsCompanies?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <InputSelect
                    name="company"
                    label={translate('makeComplaint.form.company')}
                    options={fields.optionsCompanies}
                    value={fields.company}
                    onChange={handleChange}
                    error={Boolean(error.company)}
                    helperText={error.company}
                  />
                </Grid>
              )}
              {fields.optionsCompanyRelationship.length > 0 && (
                <Grid item xs={12} md={fields.optionsCompanies?.length > 0 ? 6 : 12}>
                  <InputSelect
                    name="companyRelationship"
                    label={translate('makeComplaint.form.companyRelationship')}
                    options={fields.optionsCompanyRelationship}
                    value={fields.companyRelationship}
                    onChange={handleChange}
                    error={Boolean(error.companyRelationship)}
                    helperText={error.companyRelationship}
                  />
                </Grid>
              )}
              <Grid item xs={12} style={{display: 'none'}}>
                <InputText
                    name="complaint"
                    label={translate('makeComplaint.form.aboutTheComplaint')}
                    value={fields.im_a_robot}
                    onChange={handleChange}
                  />
              </Grid>
              <Grid item xs={12}>
                <Button onClick={handleAddReport}>
                  <IconAdd />
                  {translate('makeComplaint.form.addReports')}
                </Button>
                {!!fields.reporteds.length && (
                  <WrapReports>
                    {fields.reporteds.map((report) =>
                    <Reports key={report.id}>
                      <WrapReportTitle>
                        <TitleReport>{translate('makeComplaint.form.report')}</TitleReport>
                        {fields.reporteds.length > 1 && (
                          <ButtonRemove onClick={() => handleRemoveReport(report.id)}>
                            <IconRemove />
                          </ButtonRemove>
                        )}
                      </WrapReportTitle>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                           <InputText
                              name="name"
                              label={translate('makeComplaint.form.nameReport')}
                              value={report.name}
                              onChange={handleChangeReport(report.id)}
                              error={Boolean(error.reporteds.find((err) => err === report.id))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                           <InputText
                              name="email"
                              label={translate('makeComplaint.form.email')}
                              value={report.email}
                              onChange={handleChangeReport(report.id)}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                           <InputText
                              name="department"
                              label={translate('makeComplaint.form.department')}
                              value={report.department}
                              onChange={handleChangeReport(report.id)}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                           <InputText
                              name="position"
                              label={translate('makeComplaint.form.position')}
                              value={report.position}
                              onChange={handleChangeReport(report.id)}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                           <InputTextMasked
                              mask="phone"
                              name="phone"
                              label={translate('makeComplaint.form.phone')}
                              value={report.phone}
                              onChange={handleChangeReport(report.id)}
                            />
                        </Grid>
                      </Grid>
                      </Reports>
                    )}
                  </WrapReports>
                )}
              </Grid>
              <Grid item xs={12}>
                <InputText
                  multiline
                  minRows={4}
                  maxRows={8}
                  name="complaint"
                  label={translate('makeComplaint.form.complaint')}
                  value={fields.complaint}
                  onChange={handleChange}
                  error={Boolean(error.complaint)}
                  helperText={error.complaint}
                />
              </Grid>
              <Grid item xs={12}>
                <ButtonUpload
                  label={translate('makeComplaint.form.attach')}
                  files={fields.attachments}
                  onChange={handleChangeFile}
                  onRemove={handleRemoveFile}
                  error={Boolean(error.attachment)}
                  helperText={error.attachment}
                />
              </Grid>
            </Grid>
            <Button fullWidth type="submit">
              {translate('makeComplaint.form.send')}
            </Button>
          </Form>
        </Container>
      </Fade>
      <Confirm
        open={confirm.open}
        status={confirm.status}
        code={confirm.code}
        anonymous={confirm.anonymous}
        message={confirm.message}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirm}
      />
    </>
  );
};

const Provider = () => (
  <GoogleReCaptchaProvider reCaptchaKey={String(process.env.REACT_APP_RECAPTCHA_KEY_SITE)}>
    <MakeComplaint />
  </GoogleReCaptchaProvider>
)

export default Provider;
